// AppRoutes.js
import { lazy } from 'react';

// Components & Dashboards
const MainDashboard = lazy(() => import('../pages/dashboards/MainDashboard'));
const ComingSoon = lazy(() => import("../components/ComingSoon"));
const Devices = lazy(() => import('../pages/main-pages/Devices'));

// Transportation Module
const Transportation = lazy(() => import('../pages/dashboards/Transportations'));
const Vehicles = lazy(() => import('../pages/main-pages/Vehicles'));
const VehicleDetails = lazy(() => import('../pages/details-pages/VehicleDetails'));
const Drivers = lazy(() => import('../pages/main-pages/Drivers'));
const DriverDetails = lazy(() => import('../pages/details-pages/DriverDetails'));
const Violations = lazy(() => import('../pages/main-pages/Violations'));
const ViolationDetails = lazy(() => import('../pages/details-pages/ViolationDetails'));
const MaintenanceChecks = lazy(() => import('../pages/main-pages/MaintenanceChecks'));
const MaintenanceDetails = lazy(() => import('../pages/details-pages/MaintenanceCheckDetails'));
const LicenceChecks = lazy(() => import('../pages/main-pages/LicenceChecks'));
const LicenceDetails = lazy(() => import('../pages/details-pages/LicenceCheckDetails'));

// Tasks (Unified for Admin, Driver, and EndUser)
const Tasks = lazy(() => import('../pages/main-pages/Tasks'));
const TaskDetails = lazy(() => import('../pages/details-pages/TaskDetails'));

// Meetings Module
const Meeting = lazy(() => import('../pages/dashboards/Resevations'));
const HallBooking = lazy(() => import('../pages/main-pages/Meetings'));
const HallBookingDetails = lazy(() => import('../pages/details-pages/MeetingDetails'));
const Hospitality = lazy(() => import('../pages/main-pages/Hospitality'));
const HospitalityDetails = lazy(() => import('../pages/details-pages/HospitalityDetails'));
const Halls = lazy(() => import('../pages/main-pages/Halls'));
const HallDetails = lazy(() => import('../pages/details-pages/HallDetails'));

// Maintenance Requests Module
const MaintenanceRequest = lazy(() => import('../pages/main-pages/MaintenanceRequest'));
const MaintenanceRequestDetails = lazy(() => import('../pages/details-pages/MaintenanceRequestDetails'));
const MaintenanceRequestHistory = lazy(() => import('../pages/details-pages/MaintenanceRequestHistory'));

// Customer Happiness Module
const Feedback = lazy(() => import('../pages/main-pages/FeedbackList'));
const HomeAssessment = lazy(() => import('../pages/main-pages/HomeAssessmentList'));
const HomeAssessmentDetails = lazy(() => import('../pages/details-pages/HomeAssessmentDetails'));

// Bookings Module
const Bookings = lazy(() => import('../pages/dashboards/Bookings'));
const Hotels = lazy(() => import('../pages/main-pages/Hotels'));
const HotelDetails = lazy(() => import('../pages/details-pages/HotelDetails'));
const Suppliers = lazy(() => import('../pages/main-pages/Suppliers'));
const SupplierDetails = lazy(() => import('../pages/details-pages/SupplierDetails'));
const HotelBookings = lazy(() => import("../pages/main-pages/HotelBookings"));
const HotelBookingDetails = lazy(() => import('../pages/details-pages/HotelBookingDetails'));
const Tickets = lazy(() => import('../pages/main-pages/Tickets'));
const TicketDetails = lazy(() => import('../pages/details-pages/TicketDetails'));

// Admin specific actions
const SelectBookingSupplier = lazy(() => import('../pages/admin/SelectBookingSupplier'));
const HotelBookingSupplierReply = lazy(() => import('../pages/admin/HotelBookingSupplierReply'));
const TicketSupplierReplyAction = lazy(() => import('../pages/admin/TicketSupplierReplyAction'));

// Supplier specific actions
const HotelBookingReplyAction = lazy(() => import('../pages/supplier/HotelBookingReplyAction'));
const TicketBookingReplyAction = lazy(() => import('../pages/supplier/TicketSupplierReplyAction'));
const TicketBookingReplies = lazy(() => import('../pages/supplier/MyTicketReplies'));
const HotelBookingReplies = lazy(() => import('../pages/supplier/MyHotelReplies'));


const Administration = lazy(() => import('../pages/dashboards/Administration'));
const Users = lazy(() => import('../pages/main-pages/Users'));
const Roles = lazy(() => import('../pages/admin/Roles'));
const UserDetails = lazy(() => import('../pages/details-pages/UserDetails'));
const RoleDetails = lazy(() => import('../pages/details-pages/RoleDetails'));

const HR = lazy(() => import('../pages/dashboards/HR'));
const Vacations = lazy(() => import('../pages/main-pages/Vacations'));
const VacationDetails = lazy(() => import('../pages/details-pages/VacationDetails'));

const Subscriptions = lazy(() => import('../pages/admin/Subscriptions'));
const SubscriptionDetails = lazy(() => import('../pages/details-pages/SubscriptionDetails'));


export const privateRoutesList = [
    // --- Dashboards & Infrastructure ---
    { path: '/main_dashboard', element: MainDashboard, allowedPermissions: ['main_dashboard'] },
    { path: '/enduser_dashboard', element: ComingSoon, allowedPermissions: ['enduser_dashboard'] },
    { path: '/devices', element: Devices, allowedPermissions: ['devices'] },

    // --- Transportation Module ---
    { path: '/transportation', element: Transportation, allowedPermissions: ['transportation'] },
    { path: '/transportation/vehicles', element: Vehicles, allowedPermissions: ['vehicles'] },
    { path: '/transportation/vehicles/details/:id', element: VehicleDetails, allowedPermissions: ['vehicles'] },

    { path: '/transportation/drivers', element: Drivers, allowedPermissions: ['drivers'] },
    { path: '/transportation/drivers/details/:id', element: DriverDetails, allowedPermissions: ['drivers'] },

    { path: '/transportation/violations', element: Violations, allowedPermissions: ['violations'] },
    { path: '/transportation/violations/details/:id', element: ViolationDetails, allowedPermissions: ['violations'] },

    { path: '/transportation/maintenance_checks', element: MaintenanceChecks, allowedPermissions: ['maintenance_checks'] },
    { path: '/transportation/maintenance_checks/details/:id', element: MaintenanceDetails, allowedPermissions: ['maintenance_checks'] },

    { path: '/transportation/licence_checks', element: LicenceChecks, allowedPermissions: ['licence_checks'] },
    { path: '/transportation/licence_checks/details/:id', element: LicenceDetails, allowedPermissions: ['licence_checks'] },

    // UNIFIED TASKS (Admin, Driver, and EndUser share this path)
    { path: '/transportation/transportation_tasks', element: Tasks, allowedPermissions: ['transportation_tasks', 'mytasks', 'enduser-tasks'] },
    { path: '/transportation/transportation_tasks/details/:id', element: TaskDetails, allowedPermissions: ['transportation_tasks', 'mytasks', 'enduser-tasks'] },

    // --- Meetings Module ---
    { path: '/meetings', element: Meeting, allowedPermissions: ['meetings'], requiredService: "MEETINGS" },
    { path: '/meetings/hall_booking', element: HallBooking, allowedPermissions: ['hall_booking', 'enduser-meetings'], requiredService: "MEETINGS" },
    { path: '/meetings/hall_booking/details/:id', element: HallBookingDetails, allowedPermissions: ['hall_booking', 'enduser-meetings'], requiredService: "MEETINGS" },

    { path: '/meetings/meetings_hospitality', element: Hospitality, allowedPermissions: ['meetings_hospitality'], requiredService: "MEETINGS" },
    { path: '/meetings/meetings_hospitality/details/:id', element: HospitalityDetails, allowedPermissions: ['meetings_hospitality'], requiredService: "MEETINGS" },

    { path: '/meetings/manage_halls', element: Halls, allowedPermissions: ['manage_halls'], requiredService: "MEETINGS" },
    { path: '/meetings/manage_halls/details/:id', element: HallDetails, allowedPermissions: ['manage_halls'], requiredService: "MEETINGS" },

    // --- Maintenance Requests Module ---
    { path: '/maintenance_requests', element: MaintenanceRequest, allowedPermissions: ['maintenance_request', 'enduser_maintenance_request'] },
    { path: '/maintenance_requests/details/:id', element: MaintenanceRequestDetails, allowedPermissions: ['maintenance_request', 'enduser_maintenance_request'] },
    { path: '/maintenance_requests/history/:id', element: MaintenanceRequestHistory, allowedPermissions: ['maintenance_request', 'enduser_maintenance_request'] },

    // --- Customer Happiness Module ---
    { path: '/customer_happiness', element: ComingSoon, allowedPermissions: ['feedbacks', 'homeassessments', 'enduser_feedbacks', 'enduser_homeassessments'] },
    { path: '/customer_happiness/feedback', element: Feedback, allowedPermissions: ['feedbacks', 'enduser_feedbacks'] },
    { path: '/customer_happiness/feedback/:id', element: Feedback, allowedPermissions: ['feedbacks', 'enduser_feedbacks'] },
    { path: '/customer_happiness/home_assessment', element: HomeAssessment, allowedPermissions: ['homeassessments', 'enduser_homeassessments'] },
    { path: '/customer_happiness/home_assessment/details/:id', element: HomeAssessmentDetails, allowedPermissions: ['homeassessments', 'enduser_homeassessments'] },

    // --- Bookings Module ---
    { path: '/bookings', element: Bookings, allowedPermissions: ['bookings'] },
    { path: '/bookings/hotels', element: Hotels, allowedPermissions: ['hotels'] },
    { path: '/bookings/hotels/details/:id', element: HotelDetails, allowedPermissions: ['hotels'] },
    { path: '/bookings/suppliers', element: Suppliers, allowedPermissions: ['suppliers'] },
    { path: '/bookings/suppliers/details/:id', element: SupplierDetails, allowedPermissions: ['suppliers'] },

    // Hotel Bookings (Unified)
    { path: '/bookings/hotel_booking', element: HotelBookings, allowedPermissions: ['hotel_booking', 'enduser-hotelbookings', 'myhotelbookings'] },
    { path: '/bookings/hotel_booking/details/:id', element: HotelBookingDetails, allowedPermissions: ['hotel_booking', 'enduser-hotelbookings', 'myhotelbookings'] },

    // Ticket Bookings (Unified)
    { path: '/bookings/ticket_booking', element: Tickets, allowedPermissions: ['ticket_booking', 'enduser-ticketbookings', 'myticketbookings'] },
    { path: '/bookings/ticket_booking/details/:id', element: TicketDetails, allowedPermissions: ['ticket_booking', 'enduser-ticketbookings', 'myticketbookings'] },

    // Supplier Specific Task Pools
    { path: '/bookings/hotel_replies', element: HotelBookingReplies, allowedPermissions: ['myhotelreplies'] },
    { path: '/bookings/ticket_replies', element: TicketBookingReplies, allowedPermissions: ['myticketreplies'] },

    // Workflow Actions
    { path: '/bookings/hotel_booking/select-supplier/:id', element: SelectBookingSupplier, allowedPermissions: ['hotel_booking'], props: { type: 'hotelbooking' } },
    { path: '/bookings/ticket_booking/select-supplier/:id', element: SelectBookingSupplier, props: { type: 'ticket' }, allowedPermissions: ['ticket_booking'] },
    { path: '/bookings/hotel_booking/:id/supplier-reply/:replyId', element: HotelBookingSupplierReply, allowedPermissions: ['hotel_booking'] },
    { path: '/bookings/ticket_booking/reply/:id', element: TicketSupplierReplyAction, allowedPermissions: ['ticket_booking'] },
    { path: '/bookings/hotel_booking/reply/:id', element: HotelBookingReplyAction, allowedPermissions: ['myhotelbookings'] },
    { path: '/bookings/ticket_booking/reply/:id', element: TicketBookingReplyAction, allowedPermissions: ['myticketbookings'] },

    { path: '/administration', element: Administration, allowedPermissions: ['administration'] },
    { path: '/administration/users', element: Users, allowedPermissions: ['users'] },
    { path: '/administration/users/details/:id', element: UserDetails, allowedPermissions: ['users'] },
    { path: '/administration/roles', element: Roles, allowedPermissions: ['roles'] },
    { path: '/administration/roles/details/:id', element: RoleDetails, allowedPermissions: ['roles'] },

    { path: '/subscriptions', element: Subscriptions, allowedPermissions: ['subscriptions'] },
    { path: '/subscriptions/details/:id', element: SubscriptionDetails, allowedPermissions: ['subscriptions'] },

    // { path: '/administration/permissions', element: Roles, allowedPermissions: ['ticket_booking'] },

    // < Route path = "/administration/options" element = {< RequireAuth > <OptionsPage /> </RequireAuth >} />
    //     < Route path = "/administration/log" element = {< RequireAuth > <LogPage /> </RequireAuth >} />
    //         < Route path = "/administration/terms-and-conditions" element = {< RequireAuth > <TermsAndConditions /> </RequireAuth >} />

    { path: '/hr-management', element: HR, allowedPermissions: ['hr_management'] },
    { path: '/hr-management/vacations', element: Vacations, allowedPermissions: ['vacations'] },
    { path: '/hr-management/vacations/details/:id', element: VacationDetails, allowedPermissions: ['vacations'] },
];